import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './Hotels.css';

const Hotels = () => {
  const [hotels, setHotels] = useState([]);
  const [filteredHotels, setFilteredHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [togglingFeatured, setTogglingFeatured] = useState({});
  const [togglingStatus, setTogglingStatus] = useState({});
  const navigate = useNavigate();

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [featuredFilter, setFeaturedFilter] = useState('all');
  const [verifiedFilter, setVerifiedFilter] = useState('all');
  const [cityFilter, setCityFilter] = useState('all');
  const [starRatingFilter, setStarRatingFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchHotels();
  }, []);

  const fetchHotels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/hotels`);
      setHotels(response.data.data || []);
      setLoading(false);
    } catch (error) {
      setError('Failed to fetch hotels');
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...hotels];

    // Search by name
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(hotel => 
        hotel.name.toLowerCase().includes(query) ||
        (hotel.city && hotel.city.toLowerCase().includes(query))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(hotel => hotel.status === statusFilter);
    }

    // Featured filter
    if (featuredFilter !== 'all') {
      const isFeatured = featuredFilter === 'yes';
      filtered = filtered.filter(hotel => hotel.featured === isFeatured);
    }

    // Verified filter
    if (verifiedFilter !== 'all') {
      const isVerified = verifiedFilter === 'yes';
      filtered = filtered.filter(hotel => hotel.verified === isVerified);
    }

    // City filter
    if (cityFilter !== 'all') {
      filtered = filtered.filter(hotel => hotel.city === cityFilter);
    }

    // Star rating filter
    if (starRatingFilter !== 'all') {
      const rating = parseInt(starRatingFilter);
      filtered = filtered.filter(hotel => hotel.starRating === rating);
    }

    setFilteredHotels(filtered);
  }, [hotels, searchQuery, statusFilter, featuredFilter, verifiedFilter, cityFilter, starRatingFilter]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getUniqueCities = () => {
    const cities = [...new Set(hotels.map(hotel => hotel.city).filter(Boolean))];
    return cities.sort();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setFeaturedFilter('all');
    setVerifiedFilter('all');
    setCityFilter('all');
    setStarRatingFilter('all');
  };

  const hasActiveFilters = () => {
    return searchQuery.trim() !== '' || 
           statusFilter !== 'all' || 
           featuredFilter !== 'all' || 
           verifiedFilter !== 'all' || 
           cityFilter !== 'all' || 
           starRatingFilter !== 'all';
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this hotel?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/admin/hotels/${id}`);
      setHotels(hotels.filter(hotel => hotel._id !== id));
    } catch (error) {
      alert('Failed to delete hotel');
    }
  };

  const handleToggleFeatured = async (hotelId, currentFeaturedStatus) => {
    if (!currentFeaturedStatus) {
      // Enabling featured requires subscription - redirect to edit page
      if (window.confirm('Featured tag requires a subscription. Would you like to edit this hotel to select a subscription?')) {
        navigate(`/sy-admin/hotels/edit/${hotelId}`);
      }
      return;
    }
    
    // Disabling featured - no subscription needed
    setTogglingFeatured(prev => ({ ...prev, [hotelId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/hotels/${hotelId}`, {
        featured: false
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update the hotel in the local state
      setHotels(hotels.map(hotel => 
        hotel._id === hotelId 
          ? { ...hotel, featured: response.data.data.featured }
          : hotel
      ));
    } catch (error) {
      console.error('Error toggling featured status:', error);
      alert('Failed to update featured status');
    } finally {
      setTogglingFeatured(prev => ({ ...prev, [hotelId]: false }));
    }
  };

  const handleToggleVerified = async (hotelId, currentVerifiedStatus) => {
    if (!currentVerifiedStatus) {
      // Enabling verified requires subscription - redirect to edit page
      if (window.confirm('Verified tag requires a subscription. Would you like to edit this hotel to select a subscription?')) {
        navigate(`/sy-admin/hotels/edit/${hotelId}`);
      }
      return;
    }
    
    // Disabling verified - no subscription needed
    setTogglingFeatured(prev => ({ ...prev, [hotelId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/hotels/${hotelId}`, {
        verified: false
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update the hotel in the local state
      setHotels(hotels.map(hotel => 
        hotel._id === hotelId 
          ? { ...hotel, verified: response.data.data.verified }
          : hotel
      ));
    } catch (error) {
      console.error('Error toggling verified status:', error);
      alert('Failed to update verified status');
    } finally {
      setTogglingFeatured(prev => ({ ...prev, [hotelId]: false }));
    }
  };

  const handleToggleStatus = async (hotelId, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    setTogglingStatus(prev => ({ ...prev, [hotelId]: true }));
    
    try {
      const response = await axios.put(`${API_BASE_URL}/admin/hotels/${hotelId}`, {
        status: newStatus
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      // Update the hotel in the local state
      setHotels(hotels.map(hotel => 
        hotel._id === hotelId 
          ? { ...hotel, status: response.data.data.status }
          : hotel
      ));
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update hotel status');
    } finally {
      setTogglingStatus(prev => ({ ...prev, [hotelId]: false }));
    }
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    return `${API_BASE_URL_WITHOUT_API}${imagePath}`;
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'status-badge status-active';
      case 'inactive':
        return 'status-badge status-inactive';
      case 'pending':
        return 'status-badge status-pending';
      default:
        return 'status-badge';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="hotels-container">
          <div className="hotels-header">
            <div className="hotels-header-content">
              <h1>Hotels</h1>
              <p className="hotels-subtitle">Manage your hotel listings</p>
            </div>
            <Link to="/sy-admin/hotels/new" className="btn btn-primary btn-add">
              <span className="btn-icon">+</span>
              Add New Hotel
            </Link>
          </div>
          <div className="hotels-table-container">
            <table className="hotels-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Hotel Name</th>
                  <th>Category</th>
                  <th>City</th>
                  <th>Address</th>
                  <th>Flags</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((index) => (
                  <tr key={index} className="skeleton-row">
                    <td>
                      <div className="skeleton skeleton-image" style={{ width: '60px', height: '60px', borderRadius: '8px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '60%', height: '16px' }}></div>
                      <div className="skeleton skeleton-text" style={{ width: '40%', height: '12px', marginTop: '8px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '80px', height: '24px', borderRadius: '12px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '50%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-text" style={{ width: '90%', height: '14px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-text" style={{ width: '60px', height: '20px', borderRadius: '4px' }}></div>
                        <div className="skeleton skeleton-text" style={{ width: '60px', height: '20px', borderRadius: '4px' }}></div>
                      </div>
                    </td>
                    <td>
                      <div className="skeleton skeleton-switch" style={{ width: '100px', height: '26px' }}></div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                        <div className="skeleton skeleton-button" style={{ width: '36px', height: '36px', borderRadius: '8px' }}></div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="hotels-container">
        <div className="hotels-header">
          <div className="hotels-header-content">
            <h1>Hotels</h1>
            <p className="hotels-subtitle">Manage your hotel listings</p>
          </div>
          <Link to="/sy-admin/hotels/new" className="btn btn-primary btn-add">
            <span className="btn-icon">+</span>
            Add New Hotel
          </Link>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        {/* Filter Section */}
        {hotels.length > 0 && (
          <div className="filters-section">
            <div className="filters-header">
              <button 
                className="filters-toggle-btn"
                onClick={() => setShowFilters(!showFilters)}
              >
                <span className="filters-icon">🔍</span>
                Filters
                {hasActiveFilters() && <span className="active-filters-badge">{Object.values({
                  search: searchQuery.trim(),
                  status: statusFilter,
                  featured: featuredFilter,
                  verified: verifiedFilter,
                  city: cityFilter,
                  starRating: starRatingFilter
                }).filter(v => v !== '' && v !== 'all').length}</span>}
                <span className="toggle-arrow">{showFilters ? '▲' : '▼'}</span>
              </button>
              {hasActiveFilters() && (
                <button className="clear-filters-btn" onClick={clearFilters}>
                  Clear All
                </button>
              )}
            </div>

            {showFilters && (
              <div className="filters-content">
                <div className="filter-row">
                  <div className="filter-group">
                    <label className="filter-label">Search</label>
                    <input
                      type="text"
                      className="filter-input"
                      placeholder="Search by name or city..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Status</label>
                    <select
                      className="filter-select"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="pending">Pending</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Featured</label>
                    <select
                      className="filter-select"
                      value={featuredFilter}
                      onChange={(e) => setFeaturedFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Verified</label>
                    <select
                      className="filter-select"
                      value={verifiedFilter}
                      onChange={(e) => setVerifiedFilter(e.target.value)}
                    >
                      <option value="all">All</option>
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                </div>

                <div className="filter-row">
                  <div className="filter-group">
                    <label className="filter-label">City</label>
                    <select
                      className="filter-select"
                      value={cityFilter}
                      onChange={(e) => setCityFilter(e.target.value)}
                    >
                      <option value="all">All Cities</option>
                      {getUniqueCities().map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Star Rating</label>
                    <select
                      className="filter-select"
                      value={starRatingFilter}
                      onChange={(e) => setStarRatingFilter(e.target.value)}
                    >
                      <option value="all">All Ratings</option>
                      <option value="5">5 Stars</option>
                      <option value="4">4 Stars</option>
                      <option value="3">3 Stars</option>
                      <option value="2">2 Stars</option>
                      <option value="1">1 Star</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {hasActiveFilters() && (
              <div className="filter-results-info">
                Showing {filteredHotels.length} of {hotels.length} hotels
              </div>
            )}
          </div>
        )}

        {hotels.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🏨</div>
            <h2>No hotels found</h2>
            <p>Get started by adding your first hotel to the platform.</p>
            <Link to="/sy-admin/hotels/new" className="btn btn-primary">
              Add Your First Hotel
            </Link>
          </div>
        ) : (
          <div className="hotels-table-container">
            {filteredHotels.length === 0 && hasActiveFilters() ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <h2>No hotels match your filters</h2>
                <p>Try adjusting your filter criteria or clear all filters to see all hotels.</p>
                <button onClick={clearFilters} className="btn btn-primary">
                  Clear Filters
                </button>
              </div>
            ) : (
              <table className="hotels-table">
                <thead>
                  <tr>
                    <th>Image</th>
                    <th>Hotel Name</th>
                    <th>Category</th>
                    <th>City</th>
                    <th>Address</th>
                    <th>Flags</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHotels.map((hotel) => {
                    const isTogglingStatus = togglingStatus[hotel._id];
                    const featuredImage = hotel.featuredImage || (hotel.images && hotel.images[0]);
                    const imageUrl = getImageUrl(featuredImage);
                    const categoryName = hotel.category ? (typeof hotel.category === 'object' ? hotel.category.name : 'N/A') : 'N/A';
                    const address = hotel.displayAddress || hotel.address || 'N/A';
                    
                    return (
                      <tr key={hotel._id}>
                        <td>
                          <div className="hotel-image-cell">
                            {imageUrl ? (
                              <img src={imageUrl} alt={hotel.name} className="hotel-table-image" />
                            ) : (
                              <div className="hotel-image-placeholder">
                                <span className="placeholder-icon-small">🏨</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="hotel-name-cell">
                            <span className="hotel-name-text">{hotel.name}</span>
                          </div>
                        </td>
                        <td>
                          <div className="hotel-category-cell">
                            {categoryName !== 'N/A' ? (
                              <span className="category-badge">{categoryName}</span>
                            ) : (
                              <span className="category-empty">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="hotel-city-cell">
                            <span className="location-icon">📍</span>
                            <span>{hotel.city || 'N/A'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="hotel-address-cell">
                            <span className="address-text" title={address}>
                              {address}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="hotel-flags-cell">
                            <div className="flags-container">
                              {hotel.featured ? (
                                <span className="flag-item flag-featured" title="Featured">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                                  </svg>
                                  <span>Featured</span>
                                </span>
                              ) : (
                                <span className="flag-item flag-inactive">—</span>
                              )}
                              {hotel.verified ? (
                                <span className="flag-item flag-verified" title="Verified">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z" fill="currentColor"/>
                                  </svg>
                                  <span>Verified</span>
                                </span>
                              ) : (
                                <span className="flag-item flag-inactive">—</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="status-cell">
                            <label className="status-toggle-label">
                              <div className="toggle-switch">
                                <input
                                  type="checkbox"
                                  checked={hotel.status === 'active'}
                                  onChange={() => handleToggleStatus(hotel._id, hotel.status)}
                                  disabled={isTogglingStatus}
                                  className="toggle-input"
                                />
                                <span className={`toggle-slider ${isTogglingStatus ? 'loading' : ''}`}></span>
                              </div>
                              <span className={`status-text ${hotel.status === 'active' ? 'status-active-text' : 'status-inactive-text'}`}>
                                {hotel.status === 'active' ? 'Active' : hotel.status === 'inactive' ? 'Inactive' : 'Pending'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td>
                          <div className="table-actions">
                            <button
                              onClick={() => navigate(`/sy-admin/hotels/edit/${hotel._id}`)}
                              className="btn btn-edit btn-icon-only"
                              title="Edit Hotel"
                            >
                              <span className="btn-icon">✏️</span>
                            </button>
                            <button
                              onClick={() => handleDelete(hotel._id)}
                              className="btn btn-delete btn-icon-only"
                              title="Delete Hotel"
                            >
                              <span className="btn-icon">🗑️</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Hotels;

